/**
 * Dealer presets — canonical bundles of theme tokens applied per dealer
 * surface (showroom, landing, print). Each preset is a flat camelCase
 * record consumed by `pushThemeToDocumentElement` (bootstrap/themeApply.js),
 * which maps each key to its `--theme-*` / `--widget-*` CSS variable via
 * `THEME_MAPPING`. The shape matches the backend WidgetConfig schema so
 * a future token-driven dealer just drops a new entry here without code
 * elsewhere.
 *
 * Why this module exists:
 *   Before #965, dealer-specific accents were applied inline from page
 *   hooks (e.g. ShowroomPage.useAutodisAccent) and from scoped CSS rules
 *   (e.g. `.showroom .car-card`). That coupled "what does Autodis look
 *   like" to the page that happened to render Autodis — which is fine
 *   until a second Autodis surface (a print bundle, a deep link, a new
 *   route) needs the same look. Centralizing the values means any
 *   surface can do `usePreset('autodis')` and get the full bundle.
 *
 * Not in here:
 *   Layout that isn't dealer-themable (catalog grid gaps, header height)
 *   stays in the page CSS. Only token values that map to a CSS custom
 *   property belong here.
 */

import { pushThemeToDocumentElement } from '../bootstrap/themeApply';
import { useEffect } from 'react';

/**
 * Autodis (OMODA/JAECOO) — brand-driven near-black surface. The widget
 * embedded on Autodis pages reads white-on-black to match the catalog
 * chrome; cards и CTAs скругляются на 4px — единый минимум для салона
 * (#1022, Маша 23.06: «у нас оно минимальное, кроме ask ai»).
 */
const AUTODIS = Object.freeze({
  // Page tokens
  primaryColor: '#111111',
  primaryHover: '#000000',
  cardRadius: '4px',
  buttonRadius: '4px',
  // Widget surface — themeSync.js mirrors these onto every registered
  // shadow host, so the embedded VoiceBar inherits them.
  //
  // NB: --widget-primary / --widget-primary-hover are NOT listed here.
  // VoiceWidget builds them inline from `theme.primaryColor` (see
  // components/VoiceWidget/themes.js getThemeStyles), and inline styles
  // on `.voice-widget` win over documentElement vars — so any value we
  // push here would be silently overridden. App.jsx makes widgetTheme
  // reactive to `--theme-primary` (the page token above), which gives
  // the widget the dealer primary indirectly.
  widgetBg: '#111111',
  widgetBgHover: '#000000',
  widgetBgTertiary: '#2a2a2a',
  widgetText: '#ffffff',
  widgetTextSecondary: 'rgba(255, 255, 255, 0.7)',
});

/**
 * Registry of dealer presets. Keys are short identifiers passed to
 * `useDealerPreset(name)`; values are flat token records.
 *
 * Adding a new dealer:
 *   1. Define a preset object here.
 *   2. Call `useDealerPreset('<name>')` from the page(s) that should
 *      apply it.
 *   3. Optionally add a `--theme-*` / `--widget-*` mapping in
 *      THEME_MAPPING if the preset needs a new token category.
 */
export const DEALER_PRESETS = Object.freeze({
  autodis: AUTODIS,
});

/**
 * Apply a dealer preset for the lifetime of the calling component.
 * Snapshots whatever was previously on documentElement and restores on
 * unmount — safe to mount/unmount across routes without leaking tokens
 * into the next page.
 *
 * @param {keyof typeof DEALER_PRESETS} name
 */
export const useDealerPreset = (name) => {
  useEffect(() => {
    // Falsy name — намеренный no-op (страница вызывает хук безусловно, но
    // применяет preset условно, напр. только для Autodis/new-model машин).
    if (!name) return undefined;
    const preset = DEALER_PRESETS[name];
    if (!preset) {
      console.warn(`[dealerPresets] Unknown preset: ${name}`);
      return undefined;
    }
    return pushThemeToDocumentElement(preset);
  }, [name]);
};
